"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/ui/loader";
import PhoneNumberInput from "@/components/discover/PhoneNumberInput";
import { useEmbeddedSessionExpiry } from "@/hooks/useEmbeddedSessionExpiry";
import {
  type SpondProfileSummary,
  useSpondProfile,
} from "@/hooks/useSpondProfile";
import { todayAsIsoDate } from "@/utils/date";
import { ADULT_AGE, checkAttendeeAge } from "@/utils/eventAgeGroup";
import { getDefaultPhoneCountry, normalizePhoneNumber } from "@/utils/phone";
import { signupErrorKey } from "@/utils/signupErrorKey";
import type { CountryCode } from "libphonenumber-js";
import { useLocale, useTranslations } from "next-intl";
import { type FormEvent, useId, useState } from "react";
import ContinueInAppState from "./ContinueInAppState";

type Translate = ReturnType<typeof useTranslations>;

interface ChildDetailsStepProps {
  eventId: string;
  /** Guardian profile, shown read-only. Fetched from `/api/profile` if omitted. */
  profile?: SpondProfileSummary;
  /**
   * Called once the signup is confirmed (advances to the success step). Receives
   * the confirmed signup's `attendanceUrl` (the attendee's custom link) so the
   * funnel can send them there when they close the sheet.
   */
  onSubmitted: (attendanceUrl?: string) => void;
}

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  liability?: string;
  guardianPhone?: string;
}

interface ChildDetailsValues {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  liabilityAccepted: boolean;
  /** Set only when the guardian has to type a number (none on the profile). */
  guardianPhone?: { number: string; country: CountryCode };
}

function dateOfBirthError(dateOfBirth: string, t: Translate) {
  if (!dateOfBirth) return t("childDetails.dobRequired");
  const verdict = checkAttendeeAge({ attendeeType: "CHILD", dateOfBirth });
  if (verdict === "invalid") return t("childDetails.dobInvalid");
  if (verdict === "tooOld") {
    return t("childDetails.dobTooOld", { age: ADULT_AGE });
  }
  return undefined;
}

// Inline feedback only; Core is the authority on what a phone number is.
function guardianPhoneError(
  phone: NonNullable<ChildDetailsValues["guardianPhone"]>,
  t: Translate,
) {
  if (!phone.number.trim()) return t("phoneRequired");
  if (!normalizePhoneNumber(phone.number, phone.country)) {
    return t("phoneInvalid");
  }
  return undefined;
}

function validateChildDetails(
  values: ChildDetailsValues,
  t: Translate,
): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.firstName.trim()) {
    errors.firstName = t("childDetails.childFirstNameRequired");
  }
  if (!values.lastName.trim()) {
    errors.lastName = t("childDetails.childLastNameRequired");
  }
  const dateOfBirth = dateOfBirthError(values.dateOfBirth, t);
  if (dateOfBirth) errors.dateOfBirth = dateOfBirth;
  if (!values.liabilityAccepted) {
    errors.liability = t("childDetails.liabilityRequired");
  }
  const guardianPhone =
    values.guardianPhone && guardianPhoneError(values.guardianPhone, t);
  if (guardianPhone) errors.guardianPhone = guardianPhone;
  return errors;
}

/** `aria-describedby` target: the error node id, only while the error shows. */
function errorId(error: string | undefined, id: string) {
  return error ? id : undefined;
}

/** Child name and date of birth must not be sent to analytics. */
export default function ChildDetailsStep({
  eventId,
  profile,
  onSubmitted,
}: ChildDetailsStepProps) {
  const t = useTranslations("publicEventSignup");
  const locale = useLocale();
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";
  const liabilityId = useId();

  const {
    profile: guardian,
    loading: loadingGuardian,
    status: guardianStatus,
  } = useSpondProfile(profile);
  const sessionExpiry = useEmbeddedSessionExpiry(guardianStatus);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [liabilityAccepted, setLiabilityAccepted] = useState(false);
  const [guardianPhone, setGuardianPhone] = useState("");
  const [guardianCountry, setGuardianCountry] = useState(() =>
    getDefaultPhoneCountry(locale),
  );
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // No number on the profile — including when the profile could not be read —
  // means the guardian types one: the club has to be able to reach them, and an
  // input they can fill beats a wall they cannot pass.
  const needsGuardianPhone = !loadingGuardian && !guardian?.phoneNumber;

  function validate(): FieldErrors {
    return validateChildDetails(
      {
        firstName,
        lastName,
        dateOfBirth,
        liabilityAccepted,
        guardianPhone: needsGuardianPhone
          ? { number: guardianPhone, country: guardianCountry }
          : undefined,
      },
      t,
    );
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${basePath}/api/events/${eventId}/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attendeeType: "CHILD",
          child: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            dateOfBirth,
          },
          guardianLiabilityAccepted: true,
          signupAcknowledgementAccepted: true,
          // Always the number shown in the form: the one just typed, or the one
          // read back off the profile. Core requires it and validates it.
          guardianPhoneNumber: needsGuardianPhone
            ? normalizePhoneNumber(guardianPhone, guardianCountry)
            : guardian?.phoneNumber,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        onSubmitted(
          typeof data?.attendanceUrl === "string"
            ? data.attendanceUrl
            : undefined,
        );
        return;
      }
      // An expired session inside the WebView is only recoverable via the app.
      if (sessionExpiry.reportSubmitStatus(res.status)) return;
      setFormError(t(signupErrorKey(res.status, data?.errorKey)));
    } catch {
      setFormError(t("signupErrorGeneric"));
    } finally {
      setSubmitting(false);
    }
  }

  if (sessionExpiry.expired) {
    return <ContinueInAppState />;
  }

  const todayIso = todayAsIsoDate();

  const guardianFields = [
    { id: "g-first", label: t("firstNameLabel"), value: guardian?.firstName },
    { id: "g-last", label: t("lastNameLabel"), value: guardian?.lastName },
  ];

  return (
    <form
      onSubmit={handleSubmit}
      className="flex h-full flex-col gap-4"
      noValidate
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">{t("childDetails.header")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("childDetails.description")}
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="child-first-name">
            {t("childDetails.childFirstNameLabel")}
          </Label>
          <Input
            id="child-first-name"
            value={firstName}
            aria-invalid={!!fieldErrors.firstName}
            aria-describedby={errorId(
              fieldErrors.firstName,
              "child-first-name-error",
            )}
            onChange={(e) => setFirstName(e.target.value)}
          />
          {fieldErrors.firstName && (
            <p id="child-first-name-error" className="text-sm text-destructive">
              {fieldErrors.firstName}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="child-last-name">
            {t("childDetails.childLastNameLabel")}
          </Label>
          <Input
            id="child-last-name"
            value={lastName}
            aria-invalid={!!fieldErrors.lastName}
            aria-describedby={errorId(
              fieldErrors.lastName,
              "child-last-name-error",
            )}
            onChange={(e) => setLastName(e.target.value)}
          />
          {fieldErrors.lastName && (
            <p id="child-last-name-error" className="text-sm text-destructive">
              {fieldErrors.lastName}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="child-dob">{t("childDetails.childDobLabel")}</Label>
          <Input
            id="child-dob"
            type="date"
            className="appearance-none"
            value={dateOfBirth}
            // DOB must be in the past; cap the native picker at today so a future
            // date can't be chosen (the validate() check is the server-of-record).
            max={todayIso}
            aria-invalid={!!fieldErrors.dateOfBirth}
            aria-describedby={errorId(
              fieldErrors.dateOfBirth,
              "child-dob-error",
            )}
            onChange={(e) => setDateOfBirth(e.target.value)}
          />
          {fieldErrors.dateOfBirth && (
            <p id="child-dob-error" className="text-sm text-destructive">
              {fieldErrors.dateOfBirth}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <h3 className="font-semibold">{t("childDetails.guardianSection")}</h3>
        {loadingGuardian ? (
          <div className="flex justify-center py-2">
            <Loader />
          </div>
        ) : (
          <>
            {guardianFields.map(({ id, label, value }) => (
              <div key={id} className="flex flex-col gap-1.5">
                <Label htmlFor={`child-${id}`}>{label}</Label>
                <Input
                  id={`child-${id}`}
                  value={value ?? ""}
                  disabled
                  readOnly
                />
              </div>
            ))}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="child-g-phone">{t("phoneLabel")}</Label>
              {needsGuardianPhone ? (
                <PhoneNumberInput
                  id="child-g-phone"
                  country={guardianCountry}
                  onCountryChange={setGuardianCountry}
                  value={guardianPhone}
                  onChange={setGuardianPhone}
                  invalid={!!fieldErrors.guardianPhone}
                  describedBy={errorId(
                    fieldErrors.guardianPhone,
                    "child-g-phone-error",
                  )}
                />
              ) : (
                <Input
                  id="child-g-phone"
                  type="tel"
                  autoComplete="tel"
                  value={guardian?.phoneNumber ?? ""}
                  disabled
                  readOnly
                />
              )}
              {fieldErrors.guardianPhone && (
                <p
                  id="child-g-phone-error"
                  className="text-sm text-destructive"
                  role="alert"
                >
                  {fieldErrors.guardianPhone}
                </p>
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-start gap-2 text-sm">
          <Checkbox
            id={liabilityId}
            checked={liabilityAccepted}
            aria-invalid={!!fieldErrors.liability}
            aria-describedby={errorId(
              fieldErrors.liability,
              "child-liability-error",
            )}
            onCheckedChange={(c) => setLiabilityAccepted(c === true)}
          />
          <Label htmlFor={liabilityId} className="text-sm font-normal">
            {t("childDetails.liability")}
          </Label>
        </div>
        {fieldErrors.liability && (
          <p id="child-liability-error" className="text-sm text-destructive">
            {fieldErrors.liability}
          </p>
        )}
      </div>

      <div className="mt-auto flex flex-col gap-3 pt-4">
        {formError && (
          <p className="text-sm text-destructive" role="alert">
            {formError}
          </p>
        )}
        <Button
          type="submit"
          variant="default"
          disabled={submitting || loadingGuardian}
          className="w-full rounded-full"
        >
          {t("childDetails.submit")}
        </Button>
      </div>
    </form>
  );
}
